import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Server, Code, Cloud, Shield, Lightbulb, Headphones, Star, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { mockServices, mockStats, mockTestimonials, mockTeam } from '../mockData';

const iconMap = {
  Server, Code, Cloud, Shield, Lightbulb, Headphones
};

const Home = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-in slide-in-from-left duration-700">
              <div className="inline-block">
                <span className="bg-teal-500/10 text-teal-400 px-4 py-2 rounded-full text-sm font-medium border border-teal-500/20">
                  Innovation Technologique
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight">
                Transformez votre 
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent"> entreprise</span> avec IntegralTech
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed">
                Solutions technologiques innovantes et intégration IT sur mesure pour propulser votre croissance dans l'ère numérique.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white text-lg px-8 py-6 shadow-lg hover:shadow-teal-500/50 transition-all duration-300 group">
                    Contactez-nous
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10 text-lg px-8 py-6">
                    Découvrir nos services
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-in slide-in-from-right duration-700">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1508361727343-ca787442dcd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc2OTI5MzM4OXww&ixlib=rb-4.1.0&q=85"
                  alt="Technology"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-full h-full bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-2xl blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockStats.map((stat, index) => (
              <div key={index} className="text-center space-y-2 group cursor-pointer">
                <h3 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </h3>
                <p className="text-gray-400 text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Nos <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Des solutions technologiques complètes pour répondre à tous vos besoins en transformation digitale
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockServices.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-slate-900 border-slate-800 hover:border-teal-500/50 transition-all duration-300 group hover:shadow-xl hover:shadow-teal-500/10 cursor-pointer">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="text-teal-400" size={24} />
                    </div>
                    <CardTitle className="text-white group-hover:text-teal-400 transition-colors">{service.title}</CardTitle>
                    <CardDescription className="text-gray-400">{service.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-8 py-6 text-lg group">
                Voir tous les services
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-white">
                Pourquoi choisir <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">IntegralTech</span> ?
              </h2>
              <div className="space-y-4">
                {[
                  'Expertise technique reconnue depuis plus de 15 ans',
                  'Solutions sur mesure adaptées à vos besoins',
                  'Support technique 24/7 pour votre tranquillité',
                  'Technologies de pointe et innovation constante',
                  'Résultats mesurables et ROI garanti'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3 group cursor-pointer">
                    <CheckCircle2 className="text-teal-400 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" size={20} />
                    <p className="text-gray-300 group-hover:text-white transition-colors">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NjkyOTMzOTR8MA&ixlib=rb-4.1.0&q=85"
                alt="Team Collaboration"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Ce que disent nos <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">clients</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-slate-900 border-slate-800 hover:border-teal-500/50 transition-all duration-300 group">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-teal-400 fill-teal-400" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic">"{testimonial.content}"</p>
                  <div className="flex items-center space-x-3">
                    <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <p className="text-white font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-gray-400">{testimonial.role}, {testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Notre <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Équipe</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Des experts passionnés au service de votre réussite
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockTeam.map((member) => (
              <Card key={member.id} className="bg-slate-900 border-slate-800 hover:border-teal-500/50 transition-all duration-300 group overflow-hidden">
                <CardContent className="p-0">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
                  </div>
                  <div className="p-6 -mt-20 relative z-10">
                    <h3 className="text-xl font-bold text-white mb-2">{member.name}</h3>
                    <p className="text-teal-400 text-sm font-medium">{member.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border-teal-500/20 backdrop-blur-sm">
            <CardContent className="p-12 text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Prêt à transformer votre entreprise ?
              </h2>
              <p className="text-xl text-gray-300">
                Contactez-nous dès aujourd'hui pour discuter de vos projets et découvrir comment nous pouvons vous aider.
              </p>
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white text-lg px-10 py-6 shadow-lg hover:shadow-teal-500/50 transition-all duration-300 group">
                  Demander une consultation gratuite
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;